import React, {useMemo, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {isMobile} from 'react-device-detect';
import BtnWhiteBlackBorderBigSize from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderBigSize';
import WarningMessage from '../SharedComponents/WarningMessage';
import UpdatePaymentResultBanRow from '../SharedComponents/UpdatePaymentResultBanRow';
import {
  selectPaymentData,
  selectSelectedUpdatePaymentType,
  selectBankObligationInfo,
  selectServerData,
  selectPaymentAction,
  selectSelectedBanData,
  selectEpiData,
  handleCurrentStep,
} from '../DebtAndUpdatePaymentBlockSlice';
import {stepEnum, paymentAction} from '../Constants/debtPaymentEnum';
import {dwhUpdatePaymentMethodReport, DwhActionType, DWHMessageText, DWHStatus} from '../reports/dwhReports';
import {resolveBansFromPaymentResultList} from '../utils/resolveBansFromPaymentResultList';
import {hasAnyBanWithDebt} from '../utils/updatePaymentBanUtils';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import Image from 'app/Components/Shared/Image/image';

const PaymentNotificationText = ({text}) => {
  return <div className='s-title-small notification-value' dangerouslySetInnerHTML={{__html: text}} />;
};

const UpdatePaymentSuccess = ({onclick}) => {
  const dispatch = useDispatch();
  const paymentData = useSelector(selectPaymentData);
  const selectedUpdatePaymentType = useSelector(selectSelectedUpdatePaymentType);
  const bankObligationInfo = useSelector(selectBankObligationInfo);
  const allBans = useSelector(selectServerData);
  const paymentActionType = useSelector(selectPaymentAction);
  const selectedBanData = useSelector(selectSelectedBanData);
  const epiData = useSelector(selectEpiData);
  const loginData = useSelector((state) => state.authentication.loginData);
  const selectedBansForPaymentUpdate = useSelector((state) => state.debtAndUpdatePayment.selectedBansForPaymentUpdate);
  const phoneNumber = useSelector((state) => state.authentication.loginData?.userDetails?.contactNumber);
  const isCreditCard = selectedUpdatePaymentType === 'creditcard';

  useEffect(() => {
    if (paymentActionType !== paymentAction.UPDATE_PAYMENT_METHOD) {
      return;
    }
    const actionType = isCreditCard ? DwhActionType.SettingCreditCard : DwhActionType.SettingStandingOrder;
    dwhUpdatePaymentMethodReport({
      banData: selectedBanData,
      loginData,
      allBans,
      selectedBanNumbers: selectedBansForPaymentUpdate,
      actionType,
      messageText: DWHMessageText.Success,
      status: DWHStatus.Completed,
      lastPaymentTypeValidation: epiData?.lastPaymentTypeValidation,
    });
  }, []);

  const failedBans = useMemo(() => resolveBansFromPaymentResultList(paymentData.failedList, allBans), [paymentData.failedList, allBans]);
  const successedBans = useMemo(
    () => resolveBansFromPaymentResultList(paymentData.successedList, allBans),
    [paymentData.successedList, allBans],
  );

  const hasFailedBans = failedBans.length > 0;
  const hasAtLeastOneBanWithDebt = useMemo(() => hasAnyBanWithDebt(allBans), [allBans]);

  const getPaymentMethodText = () => {
    if (isCreditCard) {
      const lastFour = paymentData.CCFourLastNumbers?.slice(-4);
      return `<div className='d-flex'>כרטיס אשראי שמסתיים בספרות ${lastFour}</div>`;
    }

    const accountNo = String(bankObligationInfo.bankAccountNo || '');
    const lastFour = accountNo.slice(-4);

    if (lastFour) {
      return `<div className='d-flex'>הוראת קבע בחשבון בנק שמסתיים בספרות ${lastFour}</div>`;
    }

    return '<div className="d-flex">הוראת קבע בחשבון בנק</div>';
  };

  const handleFinish = () => {
    onclick?.();
    dispatch(handleCurrentStep(stepEnum.BANSELECT));
  };

  function getTitleText() {
    if (hasFailedBans) {
      return `אמצעי התשלום עודכן<br />רק בחלק מהחשבונות שביקשת`;
    }
    return 'אמצעי התשלום עודכן בהצלחה!';
  }

  return (
    <div className='debtpayment-step payment-success-step update-payment-success-step'>
      <div className='payment-success-step__icon'>
        <Image src={require('../images/success.svg')} alt='payment-success' />
      </div>

      <div
        className={`extra update-payment-success-step-title ${isMobile ? 'm-title' : 'l-title'}`}
        dangerouslySetInnerHTML={{__html: getTitleText()}}
      />

      {hasFailedBans && (
        <div className='update-payment-success-step__ban-list'>
          <div className='update-payment-success-step__ban-list-failed'>
            <div className='debtpayment-notification-title'>החשבונות שלא הצלחנו לעדכן:</div>
            {failedBans.map((ban) => (
              <UpdatePaymentResultBanRow key={`failed-${ban.banNumber}`} ban={ban} tooltipIdPrefix='update-payment-failed-ban' />
            ))}
            <div className='btn-link' type='button' onClick={isMobile ? () => (window.location = `tel:${window.Config.financeCallNumber1}`) : null}>
              לפרטים נוספים חייגו 147* לנציג שירות
            </div>
          </div>
          <div className='update-payment-result-ban-row-border'></div>
          <div className=''>
            <div className='debtpayment-notification-title'> החשבונות שעודכנו בהצלחה:</div>
            {successedBans.map((ban) => (
              <UpdatePaymentResultBanRow key={`successed-${ban.banNumber}`} ban={ban} tooltipIdPrefix='update-payment-successed-ban' />
            ))}
          </div>
        </div>
      )}

      <div className='debtpayment-notification-container'>
        <div className='debtpayment-notification-title'>אמצעי התשלום החדש:</div>
        <div className='debtpayment-notification-row payment-method-row'>
          <PaymentNotificationText text={getPaymentMethodText()} />
        </div>
        <div className='debtpayment-notification-title'>נשלח אישור:</div>
        <div className='d-flex debtpayment-notification-row'>
          <div>
            <PaymentNotificationText text={`ב-SMS למספר ${phoneNumber}`} />
            {paymentData.customerEmail && <PaymentNotificationText text={`ולכתובת מייל ${paymentData.customerEmail}`} />}
          </div>
        </div>
      </div>
      {hasAtLeastOneBanWithDebt && (
        <div className='update-payment-success-warning-notification debt-notification'>
          <WarningMessage message={'קיים חוב באחד מהחשבונות שלך'}>
            <BtnSubmit text='לתשלום החוב' onclick={handleFinish} />
          </WarningMessage>
        </div>
      )}
      {paymentData.tvNotification && (
        <div className='update-payment-success-warning-notification'>
          <WarningMessage message={`<div>כדי לחדש את הצפייה ב${window.Config.brand_Name_HE} TV - <br />יש לכבות ולהדליק את הממיר</div>`} />
        </div>
      )}
      {paymentData.cellularMsg && !paymentData.hasDebt && (
        <div className='update-payment-success-warning-notification'>
          <WarningMessage message={`<div>כדי לשחרר את קו הסלולר - <br />יש לכבות ולהדליק את הנייד שלך</div>`} />
        </div>
      )}
      {paymentData.internetMsg && !paymentData.hasDebt && (
        <div className='update-payment-success-warning-notification'>
          <WarningMessage message={`<div>כדי לשחרר את חסימת האינטרנט - <br />יש לכבות ולהדליק את הנתב</div>`} />
        </div>
      )}
      <div className='continue-button'>
        <BtnWhiteBlackBorderBigSize text='סיום' onclick={handleFinish} />
      </div>
    </div>
  );
};

export default UpdatePaymentSuccess;
